import { motion } from "framer-motion";
import { Calculator, FileSpreadsheet, PieChart, Shield } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";

const features = [
  {
    icon: <Calculator className="w-6 h-6" />,
    title: "Tax Planning & Strategy",
    description:
      "Optimize your tax position with expert planning and strategic advice tailored to your business.",
    image:
      "https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
  },
  {
    icon: <FileSpreadsheet className="w-6 h-6" />,
    title: "Bookkeeping & Payroll",
    description:
      "Streamline your financial operations with our comprehensive bookkeeping and payroll services.",
    image:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
  },
  {
    icon: <PieChart className="w-6 h-6" />,
    title: "Financial Reporting",
    description:
      "Get clear insights into your business performance with detailed financial reports and analysis.",
    image:
      "https://images.unsplash.com/photo-1543286386-713bdd548da4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Compliance & Audit",
    description:
      "Stay compliant with Australian regulations through our thorough audit and compliance services.",
    image:
      "https://images.unsplash.com/photo-1664575599736-c5197c684128?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
  },
];

const Features = () => {
  const { theme } = useTheme();

  return (
    <section className="py-24 bg-muted/30 dark:bg-gray-900/30 border-y border-border/50 dark:border-gray-800/50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl font-bold mb-5 text-foreground dark:text-white"
          >
            Comprehensive Accounting Solutions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-muted-foreground dark:text-gray-300 max-w-2xl mx-auto"
          >
            Everything you need to manage your business finances effectively
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-16">
          {/* Feature 1 */}
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="p-8 bg-card dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-border dark:border-gray-700"
            >
              <div className="w-full h-48 mb-5 rounded-lg overflow-hidden">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-foreground dark:text-white">
                  {feature.title}
                </h3>
              </div>

              <p className="text-muted-foreground dark:text-gray-300">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
